import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Cost, CostNoCarDependency, CostVersion, UpsertCost } from "./types";

export const useAdminCostsApi = () => {
  const api = useApi();

  const getAdminCost = useCallback(
    (costId: string, signal: AbortSignal) => {
      const url = `/admin/costs/${costId}`;
      return useSWRMutation<Cost | null, CustomError, string>(url, (url) => api.get<Cost>({ url, signal }));
    },
    [api],
  );

  const useAdminCosts = useCallback(
    <K extends keyof CostNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<CostNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<CostNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/costs` : "/admin/costs",
        query,
      );
      return useSWR<Paginated<NestedPick<CostNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CostNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCost = useCallback(
    async (body: UpsertCost): Promise<{ id: string }> => {
      const url = `/admin/costs`;
      const id = await api.post<UpsertCost, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}/costs`]);
      return id;
    },
    [api],
  );

  const updateAdminCost = useCallback(
    async (id: string, body: UpsertCost) => {
      const url = `/admin/costs/${id}`;
      await api.put<UpsertCost>(url, body);
      api.mutateCache([`/admin/costs`, `/admin/cars/${body.carId}/costs`, `/admin/costs/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminCost = useCallback(
    async (costId: string, carId: string): Promise<void> => {
      const url = `/admin/costs/${costId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/costs`, `/admin/cars/${carId}/costs`]);
      });
    },
    [api],
  );

  const getAdminCostHistory = useCallback(
    (query: PaginationQuery, costID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/costs/${costID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCostVersion = useCallback(
    (costId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/costs/${costId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CostVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CostVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/costs/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const importCosts = useCallback(
    async (importID: string, type: string) => {
      const url = `/admin/costs/${importID}/import/${type}`;
      await api.postNoResult(url);
      api.mutateCache([url, `/admin/costs_imports/${type}/${importID}`, `/admin/costs`]);
    },
    [api],
  );

  return {
    deleteAdminCost,
    updateAdminCost,
    insertAdminCost,
    useAdminCosts,
    getAdminCost,
    getAdminCostHistory,
    getAdminCostVersion,
    getFieldFilters,
    importCosts,
  };
};
